import ReactMarkdown from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import remarkMath from 'remark-math'
import { useState } from 'react'
import { ThoughtBlock } from './ThoughtBlock'
import { User, BookOpen, FileText, Database, ChevronDown, Trash2, Copy, Check } from 'lucide-react'
import { cn } from '../lib/utils'
import type { Artifact, PipelineInfo, TokenUsage } from '../lib/types'

interface ChatMessageProps {
  role: 'user' | 'assistant'
  content: string
  thinking?: string
  artifacts?: Artifact[]
  pipelineInfo?: PipelineInfo
  timestamp?: number
  usage?: TokenUsage
  isStreaming?: boolean
  onArtifactSelect?: (artifact: Artifact) => void
  onDelete?: () => void
}

function formatCost(cost: number): string {
  if (cost >= 0.01) return `$${cost.toFixed(3)}`
  if (cost >= 0.0001) return `$${cost.toFixed(5)}`
  return `$${cost.toExponential(2)}`
}

function formatNumber(n: number): string {
  return n.toLocaleString()
}

export function ChatMessage({
  role,
  content,
  thinking,
  artifacts,
  pipelineInfo,
  timestamp: ts,
  usage,
  isStreaming,
  onArtifactSelect,
  onDelete,
}: ChatMessageProps) {
  const isUser = role === 'user'
  const AvatarIcon = isUser ? User : BookOpen
  const [copied, setCopied] = useState(false)

  const timestamp = ts
    ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} mb-5 group animate-fade-in animate-slide-up`}>
      <div className={`flex max-w-[85%] gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} relative`}>
        <div className={`avatar ${isUser ? 'avatar-user' : 'avatar-ai'}`}>
          <AvatarIcon className="h-5 w-5" />
        </div>

        <div className={`absolute -top-2.5 ${isUser ? '-left-2.5' : '-right-2.5'} z-10 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200`}>
          <button
            onClick={handleCopy}
            className="btn btn-ghost btn-icon rounded-lg shadow-sm border border-[hsl(var(--border))] hover:bg-[hsl(var(--elevated))] hover:border-[hsl(var(--accent)/0.3)] transition-all active:scale-90"
            title="Copy message"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> : <Copy className="h-3.5 w-3.5 text-[hsl(var(--fg-muted))]" />}
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              className="btn btn-ghost btn-icon rounded-lg shadow-sm border border-[hsl(var(--border))] hover:bg-red-500/10 hover:border-red-500/30 transition-all active:scale-90"
              title="Delete message"
            >
              <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--fg-muted))] hover:text-red-400 transition-colors" />
            </button>
          )}
        </div>

        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
          <div className="mb-1 flex items-center gap-2">
            <span className="text-small font-medium text-[hsl(var(--fg-muted))]">
              {isUser ? 'You' : 'Islam Preacher'}
            </span>
            <span className="text-caption text-[hsl(var(--fg-muted))] opacity-60">
              {timestamp}
            </span>
          </div>

          {thinking && !isUser && <ThoughtBlock thinking={thinking} />}

          <div
            className={`prose prose-invert prose-sm max-w-none break-words shadow-sm transition-all duration-200 msg-bubble ${isUser ? 'msg-bubble-user' : 'hover:border-[hsl(var(--ring))/20]'}`}
          >
            <ReactMarkdown
              remarkPlugins={[remarkMath]}
              rehypePlugins={[rehypeKatex]}
              components={{
                code({ className, children, ...props }) {
                  const isInline = !className
                  if (isInline) {
                    return (
                      <code className="rounded bg-[hsl(var(--muted))] px-1 py-0.5 text-sm font-mono">
                        {children}
                      </code>
                    )
                  }
                  return (
                    <pre className="overflow-x-auto rounded-lg bg-[hsl(var(--muted))] p-4">
                      <code className={className} {...props}>
                        {children}
                      </code>
                    </pre>
                  )
                },
                a({ href, children }) {
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[hsl(var(--accent))] hover:text-[hsl(var(--accent-hover))] underline underline-offset-2"
                    >
                      {children}
                    </a>
                  )
                },
                blockquote({ children }) {
                  return (
                    <blockquote className="border-l-2 border-[hsl(var(--accent)/0.5)] pl-4 italic text-[hsl(var(--fg-muted))]">
                      {children}
                    </blockquote>
                  )
                },
              }}
            >
              {content}
            </ReactMarkdown>
            {isStreaming && <span className="inline-block h-4 w-0.5 animate-pulse bg-[hsl(var(--accent))] ml-0.5 rounded-full align-text-bottom" />}
          </div>

          {usage && !isUser && (
            <div className="badge mt-1.5 opacity-70 hover:opacity-100 transition-opacity">
              <span className="flex items-center gap-1">
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {formatNumber(usage.total_tokens)} tokens
              </span>
              <span>·</span>
              <span>
                ↑{formatNumber(usage.prompt_tokens)} ↓{formatNumber(usage.completion_tokens)}
              </span>
              {usage.cost > 0 && (
                <>
                  <span>·</span>
                  <span>{formatCost(usage.cost)}</span>
                </>
              )}
            </div>
          )}

          {artifacts && artifacts.length > 0 && !isUser && (
            <div className="mt-2 flex flex-wrap gap-2">
              {artifacts.map(artifact => (
                <button
                  key={artifact.id}
                  onClick={() => onArtifactSelect?.(artifact)}
                  className="pill border border-[hsl(var(--accent)/0.3)] hover:bg-[hsl(var(--accent-soft))] hover:scale-105 transition-all"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))]" />
                  {artifact.title}
                </button>
              ))}
            </div>
          )}

          {pipelineInfo && !isUser && <PipelineInfoBadge info={pipelineInfo} />}
        </div>
      </div>
    </div>
  )
}

function PipelineInfoBadge({ info }: { info: PipelineInfo }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="inline-flex items-center gap-1.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50 px-2.5 py-1 text-caption text-[hsl(var(--fg-muted))] hover:bg-[hsl(var(--muted))] transition-colors"
      >
        <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
        <FileText className="h-3 w-3" />
        <span>{info.transcriptChunks} transcript chunks</span>
        {info.vectorDbUsed && (
          <>
            <span className="mx-0.5">·</span>
            <Database className="h-3 w-3" />
            <span>{info.vectorDbResults} vector results</span>
          </>
        )}
      </button>
      {expanded && (
        <div className="mt-1 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 px-3 py-2 text-caption text-[hsl(var(--fg-muted))] leading-relaxed">
          {info.transcriptSources.length > 0 && (
            <p>Transcripts matched: {info.transcriptSources.join(', ')}</p>
          )}
          <p>Vector DB: {info.vectorDbUsed ? 'connected' : 'not available'} ({info.vectorDbResults} results)</p>
        </div>
      )}
    </div>
  )
}
